'use client';

import SupportActionsRenderer from '@c/dashboard/components/SupportActionsRenderer';
import MessageBubble from '@c/dashboard/MessageBubble';
import { useOffers } from '@c/dashboard/useOffers';
import { LogoWithoutText } from '@c/icons/logoWithoutText';
import ProductViewerModal from '@c/modals/ProductViewerModal';
import { useQuery } from '@tanstack/react-query';
import Avatar from '@ui/Avatar';
import {
  getChatById,
  sendMessageToChat,
  uploadChatImage,
} from '@util/firestore/messages';
import { getOrderById } from '@util/firestore/orders';
import { getProductById } from '@util/firestore/products';
import { getPublicUserDoc } from '@util/firestore/users';
import { anyAreHeic, convertHeic } from '@util/heic';
import { useConversationMutations } from '@util/hooks/useConversationMutations';
import { StatusLabel, useOnlineStatus } from '@util/hooks/useOnlineStatus';
import { cn, isMobile, isTablet } from '@util/index';
import { logError } from '@util/logError';
import { getCdnImageUrls } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { onSnapshot } from 'firebase/firestore';
import { LayoutGroup, motion } from 'framer-motion';
import {
  ChevronLeftIcon,
  CloudUploadIcon,
  MoreHorizontalIcon,
  StarIcon,
} from 'lucide-react';
import type { ChatDocument, ChatMessageDocument } from 'models/chat';
import Link from 'next/link';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ChatDetailsModal from './ChatDetails/ChatDetailsModal';
import MobileChatDetailsModal from './ChatDetails/MobileChatDetailsModal';
import ChatInput from './ChatInput';
import ChatPreviewSidebar from './ChatPreview/ChatPreviewSidebar';
import MobileChatPreview from './ChatPreview/MobileChatPreview';
import MessageImageSkeleton from './MessageImageSkeleton';

type ChatWindowProps = {
  chatId: string;
  searchTerm?: string;
  onChatArchived?: () => void;
  onGoBack?: () => void;
  isSidebar?: boolean;
};

export default function ChatWindow({
  chatId,
  searchTerm,
  onChatArchived,
  onGoBack,
  isSidebar,
}: ChatWindowProps) {
  const dragCounter = useRef<number>(0);
  const [messages, setMessages] = useState<ChatMessageDocument[] | null>(null);
  const [realtimeChat, setRealtimeChat] = useState<ChatDocument | null>(null);
  const [showChatDetailsModal, setShowChatDetailsModal] = useState(false);
  const [showMobileChatDetailsModal, setShowMobileChatDetailsModal] =
    useState(false);
  const [inputMessage, setInputMessage] = useState<string>('');
  const [fileList, setFileList] = useState<File[]>([]);
  const [convertingImages, setConvertingImages] = useState(false);
  const [uploadingImages, setUploadingImages] = useState<
    Array<{ id: string; file: File }>
  >([]);
  const [isDragging, setIsDragging] = useState(false);
  const [loadedChatPreviews, setLoadedChatPreviews] = useState<Set<string>>(
    new Set()
  );
  const [imageModal, setImageModal] = useState<{
    isOpen: boolean;
    index: number;
    images: { full: string; thumb: string; download_url?: string }[];
  }>({ isOpen: false, index: 0, images: [] });
  const messageWindowRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const inputId = useRef<string>(Math.random().toString(36).substring(7));
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  const { userDoc } = useAuth();
  const { showErrorToast } = useToastContext();

  const { data: sellerDoc, isLoading: isSellerLoading } = useQuery({
    queryKey: ['publicUser', realtimeChat?.seller_id],
    queryFn: () => getPublicUserDoc({ uid: realtimeChat?.seller_id }),
    enabled: !!realtimeChat?.seller_id,
  });

  const { data: buyerDoc, isLoading: isBuyerLoading } = useQuery({
    queryKey: ['publicUser', realtimeChat?.buyer_id],
    queryFn: () => getPublicUserDoc({ uid: realtimeChat?.buyer_id }),
    enabled: !!realtimeChat?.buyer_id,
  });

  const { data: product, isLoading: isProductLoading } = useQuery({
    queryKey: ['product', realtimeChat?.product_id ?? ''],
    queryFn: () => getProductById(realtimeChat?.product_id ?? ''),
    enabled: !!realtimeChat?.product_id,
  });

  const { offer, isLoading: isOfferLoading } = useOffers(
    realtimeChat?.offer_id
  );

  const { data: order, isLoading: isOrderLoading } = useQuery({
    queryKey: ['order', realtimeChat?.order_id ?? ''],
    queryFn: () => getOrderById(realtimeChat?.order_id ?? ''),
    enabled: !!realtimeChat?.order_id,
  });

  // Find matching messages based on search term
  const matchingMessageIndices = useMemo(() => {
    if (!searchTerm || !messages) return [];

    const indices: number[] = [];
    const lowercaseSearchTerm = searchTerm.toLowerCase();

    messages.forEach((message, index) => {
      if (message.content?.toLowerCase().includes(lowercaseSearchTerm)) {
        indices.push(index);
      }
    });

    return indices;
  }, [messages, searchTerm]);

  // Extract images from chat messages
  const chatImages = useMemo(() => {
    if (!messages) return [];

    return messages
      .filter((message) => message.is_img && message.content)
      .map((message) => message.content as string);
  }, [messages]);

  // Subscribe to real-time messages
  const chatsQuery = useMemo(() => getChatById(chatId ?? ''), [chatId]);

  useEffect(() => {
    if (!chatId) return;

    const unsubscribe = onSnapshot(chatsQuery, (snapshot) => {
      const data = snapshot.data();
      if (data) {
        // Update the complete chat document
        setRealtimeChat({ ...data, id: snapshot.id } as ChatDocument);
        setMessages(data?.messages ?? []);

        // Scroll to bottom when new messages arrive
        setTimeout(() => {
          if (messageWindowRef.current) {
            messageWindowRef.current.scrollTop =
              messageWindowRef.current.scrollHeight;
          }
        }, 100);
      } else {
        setRealtimeChat(null);
        setMessages([]);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [chatsQuery, chatId]);

  // Scroll to first matching message when search term changes or messages load
  useEffect(() => {
    if (
      matchingMessageIndices.length > 0 &&
      messages &&
      messageWindowRef.current &&
      searchTerm
    ) {
      const firstMatchIndex = matchingMessageIndices[0];
      const messageElement = messageRefs.current[firstMatchIndex];

      if (messageElement) {
        setTimeout(() => {
          messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 300); // Small delay to ensure messages are rendered
      }
    }
  }, [matchingMessageIndices, messages, searchTerm]);

  const { starChat } = useConversationMutations();
  const onImageClick = (src: string) => {
    let index: number | undefined;
    const images: { thumb: string; full: string; download_url?: string }[] = [];

    messages
      ?.filter((m) => m.is_img)
      .forEach((msg, i) => {
        if (
          index === undefined &&
          (src === msg.thumbnail || src === msg.content)
        ) {
          index = i;
        }
        images.push({
          thumb: msg.thumbnail ?? msg.content,
          full: msg.content,
          download_url: msg.download_url,
        });
      });

    if (index === undefined) index = 0;
    setImageModal({ images, index, isOpen: true });
  };

  const handleSendMessage = async () => {
    if (!userDoc || !realtimeChat?.id || convertingImages) return;

    if (
      userDoc?.roles?.support &&
      !userDoc.roles.admin &&
      !realtimeChat.case_num &&
      !realtimeChat.uids?.includes(userDoc.uid)
    ) {
      showErrorToast('You are not authorized to send messages to this chat');
      return;
    }

    // Send text message if there's text content
    if (inputMessage.trim()) {
      const messageDoc: ChatMessageDocument = {
        uid: userDoc.uid,
        content: inputMessage.trim(),
        created_at: Date.now(),
        is_img: false,
        is_auto: false,
      };
      sendMessageToChat(
        messageDoc,
        realtimeChat,
        userDoc.uid,
        product || undefined
      );
    }

    // Send image messages if there are files
    if (fileList.length > 0) {
      const files = Array.from(fileList);

      // Create optimistic uploading images
      const newUploadingImages = files.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).substring(7)}`,
        file,
      }));

      setUploadingImages((prev) => [...prev, ...newUploadingImages]);

      // Process each file
      files.forEach(async (file, index) => {
        const uploadingId = newUploadingImages[index].id;
        try {
          const uploadResult = await uploadChatImage(
            file,
            userDoc.uid,
            realtimeChat.id
          );
          const { path, download_url } = uploadResult;
          const { full, thumb } = getCdnImageUrls(path);

          const imageMessageDoc: ChatMessageDocument = {
            uid: userDoc.uid,
            content: full,
            thumbnail: thumb,
            download_url: download_url,
            created_at: Date.now(),
            is_img: true,
            is_auto: false,
          };

          await sendMessageToChat(
            imageMessageDoc,
            realtimeChat,
            userDoc.uid,
            product || undefined
          );

          // Remove from uploading state
          setUploadingImages((prev) =>
            prev.filter((img) => img.id !== uploadingId)
          );
        } catch (error) {
          logError(error);
          showErrorToast('Failed to upload image');
          // Remove from uploading state on error
          setUploadingImages((prev) =>
            prev.filter((img) => img.id !== uploadingId)
          );
        }
      });
    }

    // Reset form
    setInputMessage('');
    setFileList([]);
  };

  const handleAttachment = () => {
    const input = document.querySelector(
      `#file-input-${inputId.current}`
    ) as HTMLInputElement;
    if (input) {
      input.click();
    }
  };

  const handlePreviewImageClick = (index: number) => {
    const previewImages = fileList.map((file) => ({
      full: URL.createObjectURL(file),
      thumb: URL.createObjectURL(file),
      download_url: URL.createObjectURL(file),
    }));
    setImageModal({ images: previewImages, index, isOpen: true });
  };

  const processFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const newFiles = Array.from(files);
      const anyHeic = anyAreHeic(newFiles);
      const { heicTo } = anyHeic ? await import('heic-to') : {};

      if (anyHeic && heicTo) {
        setConvertingImages(true);
        const convertedFiles = await Promise.all(
          newFiles.map(async (file) => {
            if (/image\/hei(c|f)/.test(file.type)) {
              try {
                file = await convertHeic(heicTo, file);
              } catch (e) {
                logError(e);
                showErrorToast('Error converting HEIC image');
              }
            }
            return file;
          })
        );
        setFileList((prevFiles) => [...prevFiles, ...convertedFiles]);
        setConvertingImages(false);
      } else {
        setFileList((prevFiles) => [...prevFiles, ...newFiles]);
      }
    },
    [showErrorToast]
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    await processFiles(Array.from(e.target.files));

    // Clear the file input so the same file can be selected again if needed
    e.target.value = '';
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      const imageFiles = files.filter((file) => file.type.startsWith('image/'));

      if (imageFiles.length > 0) {
        await processFiles(imageFiles);
      }
    },
    [processFiles]
  );

  const isSupportChat = !!realtimeChat?.case_num;
  const isLoggedInAsSupport = userDoc?.roles?.support || userDoc?.roles?.admin;
  const otherPartyUser =
    realtimeChat?.seller_id === userDoc?.uid ? buyerDoc : sellerDoc;
  const isMobileDevice = isMobile() || isTablet();
  const hasPreview = !!product || !!offer || !!order;
  const isTalkingToDeletedUser =
    sellerDoc?.username.startsWith('deleted-') ||
    buyerDoc?.username.startsWith('deleted-');

  const { status } = useOnlineStatus(otherPartyUser?.uid ?? '');

  // Track which chats have had their preview loaded to prevent re-animation
  const chatPreviewKey = `${realtimeChat?.id}-${
    product?.id || offer?.id || order?.id
  }`;
  const isFirstTimeLoading =
    hasPreview && !loadedChatPreviews.has(chatPreviewKey);

  const showPreviewMessages =
    userDoc?.uid === 'FjBH0nwiHgGV4Y0EXDQS' &&
    realtimeChat?.messages?.[0]?.content.includes('New Boot Trade-In Request');

  // Mark chat preview as loaded when it appears for the first time
  useEffect(() => {
    if (hasPreview && !loadedChatPreviews.has(chatPreviewKey)) {
      setLoadedChatPreviews((prev) => new Set(prev).add(chatPreviewKey));
    }
  }, [hasPreview, chatPreviewKey, loadedChatPreviews]);

  // Auto-focus chat input when chat changes
  useEffect(() => {
    const timer = setTimeout(() => {
      chatInputRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [realtimeChat?.id]);

  if (!realtimeChat) return null;
  return (
    <LayoutGroup>
      <div
        className={cn(
          'relative flex h-full bg-white',
          isSidebar ? 'w-[55rem]' : 'w-full'
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Left Column - Chat Messages */}
        <motion.div
          layout
          className="relative flex h-full flex-1 flex-col"
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 25,
            duration: 0.8,
          }}
        >
          <div
            className={cn(
              'h-fit bg-white px-4 py-6 sm:p-8',
              !hasPreview && 'border-b border-brand-lightest-gray'
            )}
          >
            <div className="flex items-center gap-4">
              {/* Back button */}
              {onGoBack && (
                <button
                  type="button"
                  onClick={onGoBack}
                  className="flex h-12 w-12 items-center justify-center rounded-full hover:bg-zinc-100"
                  aria-label="Go back to conversations"
                >
                  <ChevronLeftIcon className="h-8 w-8 text-zinc-600" />
                </button>
              )}
              <div className="flex items-center gap-4">
                <Link
                  href={`/profile/${otherPartyUser?.username_slug}`}
                  target="_blank"
                  className="flex flex-1 flex-col gap-y-8"
                  aria-label="View conversation details"
                >
                  <div className="group flex items-center gap-4">
                    {isSellerLoading ||
                    isBuyerLoading ||
                    !sellerDoc ||
                    !buyerDoc ? (
                      <div className="h-24 w-24 animate-pulse rounded-full bg-zinc-100" />
                    ) : (
                      <>
                        {isSupportChat && !isLoggedInAsSupport ? (
                          <div className="h-24 w-24 rounded-full bg-black p-4">
                            <LogoWithoutText
                              className="h-full w-full"
                              fill="white"
                            />
                          </div>
                        ) : (
                          <Avatar
                            user={
                              realtimeChat?.seller_id === userDoc?.uid
                                ? buyerDoc
                                : sellerDoc
                            }
                            circle
                            className="h-24 w-24"
                          />
                        )}
                      </>
                    )}

                    {isSellerLoading ? (
                      <div className="flex flex-col gap-4">
                        <div className="h-4 w-48 animate-pulse rounded-lg bg-zinc-200" />

                        <div className="flex items-center gap-1">
                          <div className="h-5 w-5 animate-pulse rounded-full bg-zinc-200" />

                          <div className="h-4 w-64 animate-pulse rounded-lg bg-zinc-200" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-y-2">
                        <div className="flex items-center gap-2">
                          {isSupportChat && !isLoggedInAsSupport ? (
                            <h3 className="text-[1.6rem] font-semibold">
                              MXLocker Support
                            </h3>
                          ) : (
                            <h3
                              className={cn(
                                'max-w-[30rem] truncate text-left text-[1.6rem] font-semibold underline',
                                isTalkingToDeletedUser && 'text-red-600'
                              )}
                            >
                              {isTalkingToDeletedUser
                                ? 'Deleted User'
                                : otherPartyUser?.username}
                            </h3>
                          )}
                        </div>

                        {isSupportChat && !isLoggedInAsSupport ? (
                          <span className="text-[1.5rem] font-medium text-zinc-500">
                            Our agents typically answer within 24 hours
                          </span>
                        ) : (
                          <StatusLabel
                            status={status}
                            hide_status={!!otherPartyUser?.hide_status}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </Link>
                <button
                  type="button"
                  title="Favorite Chat"
                  className="rounded-full p-2 text-black "
                  onClick={() => {
                    starChat.mutate(realtimeChat.id);
                  }}
                >
                  {userDoc?.uid &&
                  realtimeChat.favorited?.includes(userDoc?.uid) ? (
                    <StarIcon fill="black" className="h-8 w-8" />
                  ) : (
                    <StarIcon fill="none" className="h-8 w-8" />
                  )}
                </button>
                <button
                  type="button"
                  title="More Options"
                  className="h rounded-full p-2 text-black "
                  onClick={() => {
                    if (isMobileDevice) {
                      setShowMobileChatDetailsModal(true);
                    } else {
                      setShowChatDetailsModal(true);
                    }
                  }}
                >
                  <MoreHorizontalIcon className="h-8 w-8" />
                </button>
              </div>
            </div>

            {/* Support Actions */}
            {(userDoc?.roles?.admin || userDoc?.roles?.support) && (
              <div className="mt-4 px-2 pb-2">
                <SupportActionsRenderer
                  layout={isSidebar ? 'dropdown' : 'horizontal'}
                  userDoc={userDoc}
                  recipientDoc={otherPartyUser}
                  chat={realtimeChat}
                  setChat={() => {}}
                  isAdmin={userDoc?.roles?.admin || false}
                  isSupport={
                    userDoc?.roles?.support || userDoc?.roles?.admin || false
                  }
                />
              </div>
            )}

            {showPreviewMessages && (
              <div className="flex gap-4 overflow-x-scroll whitespace-pre-wrap bg-transparent py-4 sm:flex-wrap sm:overflow-auto">
                <button
                  type="button"
                  className="rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage('How much credit are you looking for?');
                  }}
                >
                  How much credit are you looking for?
                </button>
                <button
                  type="button"
                  className="rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage('What boots do you want to go into?');
                  }}
                >
                  What boots do you want to go into?
                </button>
                <button
                  type="button"
                  className="rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'How much credit are you looking for? What boots do you want to go into?'
                    );
                  }}
                >
                  How much credit are you looking for? What boots do you want to
                  go into?
                </button>
                <button
                  type="button"
                  className="rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'Please confirm once paid, need full name and address.'
                    );
                  }}
                >
                  Please confirm once paid, need full name and address.
                </button>
                <button
                  type="button"
                  className="rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'Mind leaving us a review? https://www.trustpilot.com/review/mxlocker.com'
                    );
                  }}
                >
                  Mind leaving us a review?
                  https://www.trustpilot.com/review/mxlocker.com
                </button>
              </div>
            )}
          </div>

          {/* Top Section - Mobile Chat Preview (shown on mobile OR in sidebar mode) */}
          <div className={cn('mt-4 flex', isSidebar ? '' : 'sm:hidden')}>
            <MobileChatPreview
              product={product}
              offer={offer}
              order={order}
              userDoc={userDoc}
              isLoading={isProductLoading || isOfferLoading || isOrderLoading}
            />
          </div>

          {/* Messages */}
          <div
            ref={messageWindowRef}
            className="flex w-full flex-1 overflow-y-auto px-8 pt-8"
          >
            <div className="flex w-full grow flex-col justify-start gap-6 pb-8">
              <div className="mx-auto flex items-center justify-center gap-[1.6rem] rounded-[2rem] bg-zinc-100 px-6 py-[1.6rem] text-[1.4rem]">
                <div>
                  <LogoWithoutText className="h-16 w-16 lg:h-[2.8rem] lg:w-[2.8rem]" />
                </div>

                {product?.category === 'Dirt Bikes'
                  ? `MX Locker is not responsible for the sale of dirt bikes. Beware of scammers when selling outside the platform.`
                  : `To protect your payment, always communicate and pay through the MX Locker website or app.`}
              </div>

              <div className="h-full w-full pt-8">
                <div className="w-full pb-8">
                  {messages?.map((message, i) => {
                    const showDate =
                      i === 0 ||
                      new Date(message.created_at).toLocaleDateString() !==
                        new Date(
                          messages[i - 1].created_at
                        ).toLocaleDateString();

                    return (
                      <React.Fragment
                        key={`${message.created_at}-${message.uid}-${i}`}
                      >
                        {showDate && (
                          <div className="flex w-full items-center justify-center py-8">
                            <p className="sticky top-5 self-center text-[1.5rem] text-zinc-500">
                              {new Date(message.created_at).toLocaleDateString(
                                undefined,
                                {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                }
                              )}
                            </p>
                          </div>
                        )}

                        <div
                          className="mb-4"
                          ref={(el) => {
                            messageRefs.current[i] = el;
                          }}
                        >
                          <MessageBubble
                            chat={realtimeChat}
                            chatMessage={message}
                            realtimeMessages={messages}
                            onImageClick={onImageClick}
                            offer={offer}
                            isSidebar={isSidebar}
                          />
                        </div>
                      </React.Fragment>
                    );
                  })}

                  {/* Uploading image skeletons */}
                  {uploadingImages.map((uploadingImg) => (
                    <div
                      key={uploadingImg.id}
                      className="mb-4 flex justify-end"
                    >
                      <div className="flex max-w-[30rem] flex-col items-end gap-[1.6rem] justify-self-end font-medium sm:max-w-[50rem]">
                        <MessageImageSkeleton />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Chat Input Container */}
          <div className="flex-shrink-0">
            <ChatInput
              ref={chatInputRef}
              message={inputMessage}
              setMessage={setInputMessage}
              onSend={handleSendMessage}
              onAttachment={handleAttachment}
              placeholder="Write your message here..."
              fileList={fileList}
              onFileListChange={setFileList}
              convertingImages={convertingImages}
              uploadingImages={uploadingImages.length > 0}
              onImageClick={handlePreviewImageClick}
              chatId={realtimeChat.id}
              onDropFiles={processFiles}
              isSupport={userDoc?.roles?.support}
            />
          </div>

          <input
            id={`file-input-${inputId.current}`}
            type="file"
            accept="image/*"
            className="hidden"
            multiple={true}
            onChange={handleFileChange}
          />
        </motion.div>

        {/* Right Column - Preview Sidebar */}
        {!isSidebar && (
          <div className="hidden sm:flex">
            <ChatPreviewSidebar
              product={product}
              offer={offer}
              userDoc={userDoc}
              order={order}
              isFirstTimeLoading={isFirstTimeLoading}
              chat={realtimeChat}
              isLoading={isProductLoading || isOfferLoading || isOrderLoading}
            />
          </div>
        )}

        <ChatDetailsModal
          key={realtimeChat.id}
          open={showChatDetailsModal}
          dismiss={() => setShowChatDetailsModal(false)}
          images={chatImages}
          chat={realtimeChat}
          userDoc={userDoc}
          sellerDoc={sellerDoc}
          buyerDoc={buyerDoc}
          onArchiveSuccess={onChatArchived}
        />

        <MobileChatDetailsModal
          show={showMobileChatDetailsModal}
          dismiss={() => setShowMobileChatDetailsModal(false)}
          images={chatImages}
          chat={realtimeChat}
          userDoc={userDoc}
          onArchiveSuccess={onChatArchived}
          sellerDoc={sellerDoc}
          buyerDoc={buyerDoc}
        />

        {imageModal.isOpen && (
          <ProductViewerModal
            isOpen={imageModal.isOpen}
            dismiss={() => setImageModal({ ...imageModal, isOpen: false })}
            images={imageModal.images}
            initialImage={imageModal.index}
          />
        )}

        {/* Drag and Drop Overlay */}
        {isDragging && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-zinc-50/80 backdrop-blur-sm">
            <div className="flex flex-col items-center justify-center gap-6 rounded-3xl border-2 border-dashed border-zinc-200 bg-white/90 px-12 py-16 text-center shadow-xl">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-red-100">
                <CloudUploadIcon className="h-12 w-12 text-brand-secondary" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">
                  Drop images here
                </h3>
                <p className="mt-2 text-gray-600">
                  Release to upload your images to the chat
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutGroup>
  );
}
